import React, { useState } from 'react';
import './App.css';

export default function OptionsGreeks() {
  const [spotPrice, setSpotPrice] = useState(24350);
  const [strikePrice, setStrikePrice] = useState(24400);
  const [iv, setIv] = useState(14.5);
  const [daysToExpiry, setDaysToExpiry] = useState(5);

  // Simplified Black-Scholes / Institutional approximation for Greeks
  const calculateGreeks = () => {
    const diff = (spotPrice - strikePrice) / strikePrice;
    const timeFactor = Math.sqrt(daysToExpiry / 365);
    
    const deltaCE = +(0.5 + (diff * 2.5)).toFixed(2);
    const deltaPE = +(deltaCE - 1).toFixed(2);
    const gamma = +(0.0025 * timeFactor).toFixed(4);
    const theta = +(- (spotPrice * iv * 0.0001) / timeFactor).toFixed(2);
    const vega = +(spotPrice * 0.012 * timeFactor).toFixed(2);

    return {
      deltaCE: Math.max(0, Math.min(1, deltaCE)),
      deltaPE: Math.max(-1, Math.min(0, deltaPE)),
      gamma,
      theta,
      vega
    };
  };

  const greeks = calculateGreeks();

  return (
    <div style={{ padding: '30px', height: '100%', overflowY: 'auto', backgroundColor: 'var(--bg-main)', color: 'var(--text-main)', boxSizing: 'border-box' }}>
      <div style={{ marginBottom: '25px' }}>
        <h2 style={{ color: 'var(--vyodha-gold)', fontSize: '24px', marginBottom: '6px', fontFamily: "'Cinzel', serif" }}>
          📊 Options Greeks & Sensitivity Analyzer
        </h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: 0 }}>
          Analyze Delta, Theta decay, and Gamma exposure for precise derivatives hedging.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '25px' }}>
        <div style={{ backgroundColor: 'var(--bg-panel)', padding: '15px', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Spot Price (Nifty)</label>
          <input type="number" value={spotPrice} onChange={(e) => setSpotPrice(parseFloat(e.target.value) || 0)} style={{ width: '100%', padding: '8px', background: 'var(--bg-main)', color: 'var(--text-main)', border: '1px solid var(--border-color)', borderRadius: '6px' }} />
        </div>
        <div style={{ backgroundColor: 'var(--bg-panel)', padding: '15px', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Strike Price</label>
          <input type="number" value={strikePrice} onChange={(e) => setStrikePrice(parseFloat(e.target.value) || 0)} style={{ width: '100%', padding: '8px', background: 'var(--bg-main)', color: 'var(--text-main)', border: '1px solid var(--border-color)', borderRadius: '6px' }} />
        </div>
        <div style={{ backgroundColor: 'var(--bg-panel)', padding: '15px', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Implied Volatility (IV %)</label>
          <input type="number" value={iv} onChange={(e) => setIv(parseFloat(e.target.value) || 0)} style={{ width: '100%', padding: '8px', background: 'var(--bg-main)', color: 'var(--text-main)', border: '1px solid var(--border-color)', borderRadius: '6px' }} />
        </div>
        <div style={{ backgroundColor: 'var(--bg-panel)', padding: '15px', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Days to Expiry</label>
          <input type="number" value={daysToExpiry} onChange={(e) => setDaysToExpiry(parseInt(e.target.value) || 1)} style={{ width: '100%', padding: '8px', background: 'var(--bg-main)', color: 'var(--text-main)', border: '1px solid var(--border-color)', borderRadius: '6px' }} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '15px' }}>
        <div style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid #00C853', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Call Delta ($\Delta$)</span>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#00C853', marginTop: '8px' }}>{greeks.deltaCE}</div>
        </div>
        <div style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid #FF1744', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Put Delta ($\Delta$)</span>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#FF1744', marginTop: '8px' }}>{greeks.deltaPE}</div>
        </div>
        <div style={{ backgroundColor: 'var(--bg-panel)', border: 'var(--border-color)', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Gamma ($\Gamma$)</span>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--vyodha-gold)', marginTop: '8px' }}>{greeks.gamma}</div>
        </div>
        <div style={{ backgroundColor: 'var(--bg-panel)', border: 'var(--border-color)', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Theta ($\Theta$ - Decay)</span>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#FF1744', marginTop: '8px' }}>₹ {greeks.theta}</div>
        </div>
        <div style={{ backgroundColor: 'var(--bg-panel)', border: 'var(--border-color)', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Vega ($\nu$)</span>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--vyodha-gold)', marginTop: '8px' }}>₹ {greeks.vega}</div>
        </div>
      </div>
    </div>
  );
}